import React from "react";
import { FiCpu, FiTrendingDown, FiAlertOctagon, FiClock, FiBarChart2, FiBattery } from "react-icons/fi";
import { HealthRing } from "./irrigationHelpers";

const PRIORITY_META = {
  High:   { color: "#ef4444", bg: "#fee2e2", label: "High Priority" },
  Medium: { color: "#f59e0b", bg: "#fef3c7", label: "Medium Priority" },
  Low:    { color: "#3b82f6", bg: "#dbeafe", label: "Low Priority" },
};

const ICONS = { "💧": FiTrendingDown, "🌧": FiAlertOctagon, "⚙": FiAlertOctagon, "⏰": FiClock, "📊": FiBarChart2, "🔋": FiBattery };

const IrrAiPanel = ({ d }) => {
  const { aiInsights } = d;
  const systemScore = d.summary.healthScore;
  const waterSaved  = 18400;  // litres saved this week vs traditional
  const efficiency  = 84;     // overall AI-driven efficiency %

  return (
    <div className="irr-ai-panel">

      {/* ── Header score cards ── */}
      <div className="irr-ai-scores">
        <div className="irr-ai-score-card">
          <HealthRing score={systemScore} size={90} />
          <div>
            <div className="irr-ai-sc-title">System Intelligence Score</div>
            <div className="irr-ai-sc-sub">Combines valve health, node connectivity, soil moisture accuracy and scheduling efficiency</div>
          </div>
        </div>
        <div className="irr-ai-score-card">
          <HealthRing score={efficiency} size={90} />
          <div>
            <div className="irr-ai-sc-title">Water Use Efficiency</div>
            <div className="irr-ai-sc-sub">AI-optimized schedules vs. fixed-timer baseline — {efficiency}% efficiency this week</div>
          </div>
        </div>
        <div className="irr-ai-savings-card">
          <div className="irr-ai-sav-val">{(waterSaved / 1000).toFixed(1)} kL</div>
          <div className="irr-ai-sav-lbl">Water Saved This Week</div>
          <div className="irr-ai-sav-sub">vs. traditional fixed-schedule irrigation</div>
          <div className="irr-ai-sav-money">≈ ₹{(waterSaved * 0.02).toFixed(0)} saved</div>
        </div>
      </div>

      {/* ── AI Capability description ── */}
      <div className="irr-ai-capabilities">
        <div className="irr-section-title">AI Irrigation Engine — Capabilities</div>
        <div className="irr-ai-cap-grid">
          {[
            { title: "Optimal Time Prediction",   desc: "Analyses evapotranspiration, humidity and temperature to find lowest-loss irrigation windows" },
            { title: "Over-watering Prevention",  desc: "Compares zone moisture curves to prevent waterlogging and nutrient leaching" },
            { title: "Pressure Anomaly Detection",desc: "Detects subtle pressure drops that precede valve failure or pipe bursts" },
            { title: "Valve Failure Prediction",  desc: "Tracks valve response latency trend — alerts 5–7 days before predicted failure" },
            { title: "Weather Integration",       desc: "Integrates 48h rain forecast to skip or reduce irrigation cycles automatically" },
            { title: "Water Cost Optimization",   desc: "Balances crop water needs with borewell pumping cost per hour" },
          ].map(cap => (
            <div key={cap.title} className="irr-ai-cap-card">
              <FiCpu size={16} color="#667eea" />
              <div>
                <div className="irr-ai-cap-title">{cap.title}</div>
                <div className="irr-ai-cap-desc">{cap.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Insight cards ── */}
      <div className="irr-section-title" style={{ marginTop: 20 }}>Active AI Recommendations</div>
      <div className="irr-insight-list">
        {aiInsights.map(ins => {
          const pm = PRIORITY_META[ins.priority];
          return (
            <div key={ins.id} className="irr-insight-card" style={{ borderLeftColor: pm.color }}>
              <div className="irr-ins-top">
                <span className="irr-ins-icon">{ins.icon}</span>
                <div>
                  <div className="irr-ins-title">{ins.title}</div>
                  <span className="irr-badge" style={{ background: pm.bg, color: pm.color, fontSize: 11 }}>{pm.label}</span>
                </div>
              </div>
              <div className="irr-ins-text">{ins.insight}</div>
              <div className="irr-ins-footer">
                <span className="irr-ins-id">{ins.id}</span>
                <button className="irr-ctrl-btn irr-ctrl-purple" style={{ padding: "4px 12px", fontSize: 12 }}>{ins.action}</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── AI prediction note ── */}
      <div className="irr-ai-footer-note">
        <FiCpu size={14} color="#667eea" />
        AI engine runs every 15 minutes using local sensor data. No cloud dependency — predictions work even when internet is offline.
      </div>
    </div>
  );
};

export default IrrAiPanel;
